import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, User as UserIcon, MessageSquare, FileText, Home as HomeIcon,
  Heart, CalendarClock, ExternalLink, Loader2, Send, ArrowRight, X, Eye, Plus,
  Signature, CheckCircle2, ClockIcon, AlertTriangle, Download, Activity as ActivityIcon,
  StickyNote, Save, Wand2, Search, Trash2, CornerDownRight, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SignatureChip } from "../components/SignatureChip";
import { EmailLink, PhoneLink } from "../components/ContactLinks";
import PdfFillDialog from "../components/PdfFillDialog";
import ClientEdit from "../components/ClientEdit";
import DocPreviewModal from "../components/DocPreviewModal";

// Portal-preview keys — mirrors Clients.jsx so "View portal" jumps straight
// into an impersonated portal session for this client.
const PORTAL_TOKEN_KEY = "realtor_os_portal_token";
const PORTAL_AGENT_KEY = "realtor_os_portal_agent";
const PORTAL_CLIENT_KEY = "realtor_os_portal_client";
const PORTAL_BRAND_KEY = "realtor_os_portal_brand";
const IMPERSONATE_RETURN_KEY = "realtor_os_impersonate_return_path";
const IMPERSONATE_ACTIVE_KEY = "realtor_os_impersonate_active";

/**
 * Client 360 — a single-glance dashboard for one client.
 *
 * Everything the agent needs to know appears here: contact, active
 * transactions, all documents (regardless of draft state), recommended
 * listings with engagement signals, and message thread previews. Every
 * summary card is a jumping-off point — click a doc name to preview it,
 * click a listing to open the source URL, click a transaction to open the
 * full workshop.
 */
export default function ClientDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const highlightListingId = searchParams.get("listing");
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null); // { id, name }
  const [editingClient, setEditingClient] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/clients/${id}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load client");
      nav("/clients");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const previewAsClient = async () => {
    try {
      const { data: r } = await api.post(`/clients/${id}/impersonate-portal`);
      localStorage.setItem(PORTAL_TOKEN_KEY, r.token);
      localStorage.setItem(PORTAL_CLIENT_KEY, JSON.stringify(r.client));
      localStorage.setItem(PORTAL_AGENT_KEY, JSON.stringify(r.agent));
      if (r.brokerage) {
        localStorage.setItem("realtor_os_portal_brokerage", JSON.stringify(r.brokerage));
      } else {
        localStorage.removeItem("realtor_os_portal_brokerage");
      }
      if (r.disclosure) {
        localStorage.setItem("realtor_os_portal_disclosure", JSON.stringify(r.disclosure));
      } else {
        localStorage.removeItem("realtor_os_portal_disclosure");
      }
      if (r.agent?.brand_color) localStorage.setItem(PORTAL_BRAND_KEY, r.agent.brand_color);
      localStorage.setItem(IMPERSONATE_RETURN_KEY, window.location.pathname);
      localStorage.setItem(IMPERSONATE_ACTIVE_KEY, "1");
      nav("/portal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not preview");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500 text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading client…
      </div>
    );
  }
  if (!data) return null;

  const { client, transactions, documents, listings, threads, stats, activities = [] } = data;

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Link to="/clients" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="c360-back">
            <ArrowLeft className="h-3 w-3" /> All clients
          </Link>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter text-slate-900" data-testid="c360-name">{client.full_name}</h1>
          <div className="mt-1 flex items-center gap-4 flex-wrap text-sm text-slate-600">
            <EmailLink email={client.email} testId="c360-email" />
            <PhoneLink phone={client.phone} testId="c360-phone" />
            {client.client_type && (
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 border border-slate-200 rounded px-1.5 py-0.5" data-testid="c360-type">
                {client.client_type === "both" ? "Buyer · Seller" : client.client_type}
              </span>
            )}
            {client.lead_name && (
              <Link
                to={`/leads/${client.lead_id}`}
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-900 border border-slate-200 rounded px-1.5 py-0.5"
                data-testid="c360-lead-backlink"
              >
                From lead <ArrowRight className="h-2.5 w-2.5" />
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setEditingClient(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="c360-edit-client"
          >
            <Pencil className="h-4 w-4" /> Edit client
          </button>
          <button
            onClick={previewAsClient}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="c360-view-as-client"
          >
            <Eye className="h-4 w-4" /> View as client
          </button>
        </div>
      </div>

      {/* ── Contact & preferences ─────────────────────────────────────
          The Edit-client modal writes here; every non-empty field
          collected on the form should be visible somewhere on the
          page so agents can see edits landed. Empty fields collapse
          silently — no "Not provided" clutter, just a nudge to open
          the modal if the panel is empty. */}
      <ClientInfoPanel client={client} onEdit={() => setEditingClient(true)} />

      {/* ── Snapshot stat strip ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="c360-stats">
        <Stat label="Transactions" value={stats.transactions} icon={<HomeIcon className="h-4 w-4" />} />
        <Stat label="Documents" value={stats.documents} sub={stats.documents_signed ? `${stats.documents_signed} signed · ${stats.documents_awaiting} awaiting` : `${stats.documents_awaiting} awaiting`} icon={<FileText className="h-4 w-4" />} />
        <Stat label="Recommended" value={stats.listings} sub={stats.listings_liked ? `${stats.listings_liked} liked · ${stats.showings_requested} showings` : null} icon={<HomeIcon className="h-4 w-4" />} />
        <Stat label="Messages" value={stats.unread_messages} sub={stats.unread_messages ? "unread" : "all read"} icon={<MessageSquare className="h-4 w-4" />} highlight={stats.unread_messages > 0} />
      </div>

      {/* ── Notes strip ───────────────────────────────────────────────── */}
      <ClientNotesStrip clientId={client.id} initialNotes={client.notes || ""} onSaved={(n) => setData(d => ({ ...d, client: { ...d.client, notes: n } }))} />

      {/* ── Two-column layout ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <TransactionsCard transactions={transactions} />
        </div>
        <div className="space-y-6">
          <MessagesCard threads={threads} clientName={client.full_name} />
          <ListingsCard listings={listings} highlightId={highlightListingId} />
          <ActivityCard activities={activities} />
        </div>
      </div>

      {/* Documents — full-width at the bottom of the page.
          Docs are the densest block (signing status chips, multiple rows
          per doc) and deserve the full page width. Placing them here keeps
          the fold above focused on client identity, transactions and the
          engagement signals that trigger the agent's next action. */}
      <DocumentsCard
        client={client}
        documents={documents}
        transactions={transactions}
        onPreview={setPreviewDoc}
        onReload={load}
      />

      {previewDoc && (
        <DocPreviewModal
          docId={previewDoc.id}
          name={previewDoc.name}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {editingClient && (
        <ClientEdit
          client={client}
          onClose={() => setEditingClient(false)}
          onSaved={() => { setEditingClient(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Contact & preferences panel ───────────────────────────────────────
// Renders every editable field the Edit-client modal collects. Empty
// fields collapse cleanly — we don't want an "unset" wall the first
// time an agent lands on a lightly-populated client. If nothing at all
// is filled beyond email/phone, the panel folds into a single "Add
// details" nudge that opens the modal.
function ClientInfoPanel({ client, onEdit }) {
  const fmtMoney = (n) => (n == null || n === "" ? null : `$${Number(n).toLocaleString()}`);
  const priceRange = (() => {
    const lo = fmtMoney(client.budget_min);
    const hi = fmtMoney(client.budget_max);
    if (lo && hi) return `${lo} – ${hi}`;
    if (lo) return `${lo}+`;
    if (hi) return `Up to ${hi}`;
    return null;
  })();

  const fields = [
    { key: "address", label: "Address", value: client.address, testId: "c360-info-address" },
    { key: "source", label: "Source", value: client.source, testId: "c360-info-source" },
    { key: "price", label: "Price range", value: priceRange, testId: "c360-info-price" },
    { key: "interest", label: "Property interest", value: client.property_interest, testId: "c360-info-interest", full: true },
  ];
  const populated = fields.filter(f => f.value);

  if (populated.length === 0) {
    return (
      <section className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 flex items-center justify-between gap-3" data-testid="c360-info-empty">
        <div className="text-sm text-slate-600">
          <b className="text-slate-900">No preferences on file yet.</b> Add address, budget, or property interest to unlock Smart Fill and sharper recommendations.
        </div>
        <button
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white shrink-0"
          style={{ background: "var(--primary-hex)" }}
          data-testid="c360-info-empty-edit"
        >
          <Pencil className="h-3.5 w-3.5" /> Add details
        </button>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="c360-info-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900 flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-slate-500" /> Contact & preferences
        </h2>
        <button
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded-md text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2 py-1"
          data-testid="c360-info-edit"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
        {populated.map(f => (
          <div key={f.key} className={f.full ? "sm:col-span-2 lg:col-span-3" : ""} data-testid={f.testId}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">{f.label}</div>
            <div className={`text-sm text-slate-900 ${f.full ? "whitespace-pre-wrap leading-relaxed" : "truncate"}`}>
              {f.value}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Free-text notes ───────────────────────────────────────────────────
// A 30-second reminder strip that never lives inside a transaction. Auto-
// saves 700ms after the agent stops typing so nothing ever needs a manual
// "save" click, but a Save button is exposed for people who like buttons.
function ClientNotesStrip({ clientId, initialNotes, onSaved }) {
  const [value, setValue] = useState(initialNotes || "");
  const [status, setStatus] = useState("clean"); // clean | dirty | saving | saved | error
  const initialRef = useRef(initialNotes || "");

  useEffect(() => {
    initialRef.current = initialNotes || "";
    setValue(initialNotes || "");
    setStatus("clean");
  }, [initialNotes, clientId]);

  // Debounced auto-save.
  useEffect(() => {
    if (value === initialRef.current) return;
    setStatus("dirty");
    const t = setTimeout(async () => {
      setStatus("saving");
      try {
        const { data } = await api.patch(`/clients/${clientId}`, { notes: value });
        initialRef.current = data.notes || "";
        setStatus("saved");
        onSaved?.(data.notes || "");
        setTimeout(() => setStatus(s => (s === "saved" ? "clean" : s)), 1500);
      } catch (e) {
        setStatus("error");
        toast.error(e?.response?.data?.detail || "Couldn't save notes");
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const statusText = {
    clean: "",
    dirty: "Unsaved",
    saving: "Saving…",
    saved: "Saved",
    error: "Save failed",
  }[status];

  const statusTone = {
    dirty: "text-amber-700",
    saving: "text-slate-500",
    saved: "text-emerald-700",
    error: "text-rose-700",
  }[status] || "text-slate-500";

  return (
    <section className="rounded-lg border border-slate-200 bg-amber-50/40 p-4" data-testid="c360-notes">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <StickyNote className="h-4 w-4 text-amber-600" />
          Client notes
          <span className="text-[10px] font-medium normal-case tracking-normal text-slate-500 hidden sm:inline">· only you see this</span>
        </div>
        <div className={`text-[11px] font-medium ${statusTone} inline-flex items-center gap-1 min-w-[52px] justify-end`} data-testid="c360-notes-status">
          {status === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
          {status === "saved" && <CheckCircle2 className="h-3 w-3" />}
          {statusText}
        </div>
      </div>
      <textarea
        value={value}
        onChange={e => setValue(e.target.value)}
        rows={2}
        placeholder="Kids' names, allergies, showing preferences, anything that helps you sound like you remember them."
        className="w-full rounded-md border border-slate-200 bg-white/70 focus:bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus-brand resize-y"
        data-testid="c360-notes-input"
      />
    </section>
  );
}

// ── Snapshot stats ─────────────────────────────────────────────────────
function Stat({ label, value, sub, icon, highlight }) {
  return (
    <div className={`rounded-lg border ${highlight ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"} p-3`} data-testid={`c360-stat-${label.toLowerCase()}`}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="mt-1 font-heading text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Transactions ───────────────────────────────────────────────────────
function TransactionsCard({ transactions }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="c360-transactions">
      <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900 flex items-center gap-2">
        <HomeIcon className="h-4 w-4 text-slate-500" /> Active transactions
      </h2>
      {transactions.length === 0 ? (
        <div className="mt-3 text-sm text-slate-500">No transactions yet — start one from the Clients page.</div>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {transactions.map(tx => (
            <li key={tx.id}>
              <Link
                to={`/transactions/${tx.id}`}
                className="flex items-center gap-3 py-3 hover:bg-slate-50 -mx-2 px-2 rounded"
                data-testid={`c360-tx-${tx.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{tx.property_address || "Property TBD"}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                    <span className="uppercase tracking-widest text-[10px] font-semibold">{tx.side}</span>
                    <span>·</span>
                    <span className="uppercase tracking-widest text-[10px] font-semibold">{(tx.status || "").replace(/_/g, " ")}</span>
                    {tx.close_date && (
                      <>
                        <span>·</span>
                        <span>closes {new Date(tx.close_date).toLocaleDateString()}</span>
                      </>
                    )}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Documents ──────────────────────────────────────────────────────────
// Split into two sections:
//  • Client-level documents  — attached directly to the client record
//    (Vault-added evergreen paperwork: buyer rep, listing prep,
//    disclosures). Agent can Add from Vault, Smart Fill, or send for
//    signature to THIS client directly (no transaction required).
//  • Deal documents          — docs living on the client's transactions.
//    Read-only here; agent clicks through to the transaction workshop
//    to modify them.
//
// Signed copies (auto-filed by the DocuSign webhook when an envelope
// completes) are NOT surfaced as standalone rows. Instead, we nest them
// visually under the original so agents see the "Original → Signed"
// chain in one place and don't have to hunt through the list. Deletes
// use a two-tap confirm pattern: first tap arms, second tap deletes.
function DocumentsCard({ client, documents, transactions, onPreview, onReload }) {
  const txMap = useMemo(() => Object.fromEntries(transactions.map(t => [t.id, t])), [transactions]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fillDoc, setFillDoc] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Build id→doc map + "which docs are signed copies of another" set so
  // signed copies don't render as top-level rows (they're nested below
  // their original). Also lets us pull the signed copy by original id.
  const docsById = useMemo(() => Object.fromEntries(documents.map(d => [d.id, d])), [documents]);
  const signedCopyIds = useMemo(
    () => new Set(documents.map(d => d.signed_document_id).filter(Boolean)),
    [documents],
  );

  const topLevel = documents.filter(d => !signedCopyIds.has(d.id));
  const clientDocs = topLevel.filter(d => d.client_id === client.id && !d.transaction_id);
  const dealDocs = topLevel.filter(d => !!d.transaction_id);

  const isPdf = (d) => (d.mime_type || "").includes("pdf") || (d.name || "").toLowerCase().endsWith(".pdf");

  const sendForSignature = async (id) => {
    setSendingId(id);
    try {
      await api.post("/docusign/send-auto", { document_id: id });
      toast.success("Sent to the client's portal — they'll see a Sign now button and get an in-app notification.");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send for signature");
    } finally {
      setSendingId(null);
    }
  };

  const deleteDoc = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document deleted");
      setConfirmDelete(null);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
      setConfirmDelete(null);
    }
  };

  const download = async (id) => {
    const { data } = await api.get(`/documents/${id}/download`);
    window.open(data.url, "_blank");
  };

  const renderSignedCopy = (signedId) => {
    const s = docsById[signedId];
    if (!s) return null;
    return (
      <div
        className="mt-2 ml-6 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-center gap-2"
        data-testid={`c360-doc-signed-${s.id}`}
      >
        <CornerDownRight className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
        <FileText className="h-4 w-4 text-emerald-700 shrink-0" />
        <button
          type="button"
          onClick={() => onPreview({ id: s.id, name: s.name })}
          className="flex-1 min-w-0 text-left"
          data-testid={`c360-doc-signed-open-${s.id}`}
        >
          <div className="text-sm font-semibold text-emerald-900 truncate hover:underline">{s.name}</div>
          <div className="text-[11px] text-emerald-800/80 mt-0.5">
            Signed copy · filed {new Date(s.created_at).toLocaleDateString()}
          </div>
        </button>
        <button
          type="button"
          onClick={() => download(s.id)}
          className="text-emerald-700 hover:text-emerald-900 p-1"
          title="Download signed copy"
          data-testid={`c360-doc-signed-dl-${s.id}`}
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
    );
  };

  const renderRow = (d, { showActions, allowDelete }) => {
    const tx = d.transaction_id ? txMap[d.transaction_id] : null;
    const pdf = isPdf(d);
    const armed = confirmDelete === d.id;
    return (
      <li key={d.id} className="py-3" data-testid={`c360-doc-${d.id}`}>
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <button
              type="button"
              onClick={() => pdf ? onPreview({ id: d.id, name: d.name }) : window.open(`/api/v1/documents/${d.id}/download`, "_blank")}
              className="font-semibold text-slate-900 text-sm truncate hover:underline text-left w-full"
              title="Open preview"
              data-testid={`c360-doc-open-${d.id}`}
            >
              {d.name}
            </button>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              {tx && (
                <Link to={`/transactions/${tx.id}`} className="hover:underline text-slate-500" data-testid={`c360-doc-tx-${d.id}`}>
                  {tx.property_address || "Transaction"}
                </Link>
              )}
              {d.filled_from_document_id && (
                <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded uppercase tracking-widest">filled</span>
              )}
              <SignatureChip status={d.signature_status} testId={`c360-doc-sig-${d.id}`} />
              <span>· added {new Date(d.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          {showActions && (
            <div className="flex items-center gap-1 shrink-0">
              {pdf && !d.signature_status && (
                <button
                  type="button"
                  onClick={() => setFillDoc({ id: d.id, name: d.name })}
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md px-2 py-1 text-xs font-semibold"
                  title="Smart Fill this document with the agent + client data"
                  data-testid={`c360-doc-fill-${d.id}`}
                >
                  <Wand2 className="h-3.5 w-3.5" /> Smart Fill
                </button>
              )}
              {pdf && (
                <button
                  type="button"
                  onClick={() => sendForSignature(d.id)}
                  disabled={sendingId === d.id || d.signature_status === "sent" || d.signature_status === "completed"}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "var(--primary-hex)" }}
                  title={d.signature_status === "sent" ? "Already awaiting signature" : "Send to DocuSign"}
                  data-testid={`c360-doc-send-${d.id}`}
                >
                  {sendingId === d.id ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="h-3.5 w-3.5" /> Send to DocuSign</>
                  )}
                </button>
              )}
            </div>
          )}
          {allowDelete && (
            <button
              type="button"
              onClick={() => armed ? deleteDoc(d.id) : setConfirmDelete(d.id)}
              onBlur={() => armed && setConfirmDelete(null)}
              className={`shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-md ${armed ? "bg-red-600 text-white" : "text-slate-400 hover:text-red-700 hover:bg-slate-100"}`}
              title={armed ? "Tap again to confirm" : "Delete document"}
              data-testid={`c360-doc-delete-${d.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        {d.signed_document_id && renderSignedCopy(d.signed_document_id)}
      </li>
    );
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="c360-documents">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900 flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-500" /> Documents
        </h2>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-white"
          style={{ background: "var(--primary-hex)" }}
          data-testid="c360-add-from-vault-btn"
        >
          <Plus className="h-4 w-4" /> Add from Vault
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Client-level paperwork (buyer rep, listing prep, disclosures) lives here. Deal-specific docs stay on the transaction workshop.
      </p>

      {/* Client-level documents */}
      <div className="mt-4" data-testid="c360-client-docs">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Client documents</div>
        {clientDocs.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center" data-testid="c360-client-docs-empty">
            <FileText className="h-6 w-6 mx-auto text-slate-300" />
            <p className="mt-2 text-xs text-slate-600">
              No client-level documents yet. Click <b>Add from Vault</b> to pull one in.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {clientDocs.map(d => renderRow(d, { showActions: true, allowDelete: true }))}
          </ul>
        )}
      </div>

      {/* Deal documents (transaction-scoped, read-only actions here) */}
      {dealDocs.length > 0 && (
        <div className="mt-6" data-testid="c360-deal-docs">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Deal documents</div>
          <ul className="divide-y divide-slate-100">
            {dealDocs.slice(0, 12).map(d => renderRow(d, { showActions: false, allowDelete: false }))}
          </ul>
          {dealDocs.length > 12 && (
            <div className="mt-3 text-xs text-slate-500 text-center">Showing 12 of {dealDocs.length} — open a transaction for the full list.</div>
          )}
        </div>
      )}

      {pickerOpen && (
        <AddFromVaultModal
          clientId={client.id}
          onClose={() => setPickerOpen(false)}
          onAdded={() => { setPickerOpen(false); onReload(); }}
        />
      )}

      {fillDoc && (
        <PdfFillDialog
          docId={fillDoc.id}
          docName={fillDoc.name}
          onClose={() => setFillDoc(null)}
          onFilled={() => onReload()}
          onSendForSignature={(newDocId) => sendForSignature(newDocId)}
        />
      )}
    </section>
  );
}

// ── Add from Vault modal — client-scoped ──────────────────────────────
// Mirrors the transaction-side "Add from Vault" flow but posts a
// `client_id` to /form-templates/{id}/assign so the fresh copy lands on
// the client record instead of a specific deal.
function AddFromVaultModal({ clientId, onClose, onAdded }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [assigningId, setAssigningId] = useState(null);

  useEffect(() => {
    api.get("/form-templates")
      .then(({ data }) => setTemplates(data))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = templates.filter(t => {
    if (!q.trim()) return true;
    return (t.name || "").toLowerCase().includes(q.trim().toLowerCase());
  });

  const assign = async (tplId) => {
    setAssigningId(tplId);
    try {
      const { data } = await api.post(`/form-templates/${tplId}/assign`, { client_id: clientId });
      toast.success(data.reused ? "Already added to this client" : "Copy added to this client");
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="c360-add-from-vault-modal">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Master Library</div>
            <h3 className="font-heading text-xl font-bold text-slate-900">Add from Vault</h3>
            <p className="mt-1 text-xs text-slate-500">Pick a master template — we'll create a fresh, client-scoped copy on this client's file.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="c360-add-from-vault-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search templates by name…"
              className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus-brand"
              data-testid="c360-add-from-vault-search"
            />
          </div>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {loading && <div className="p-6 text-sm text-slate-500 text-center flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading Vault…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              {templates.length === 0
                ? <>Your Vault is empty. <Link to="/documents" className="font-semibold text-slate-900 hover:underline">Upload a template →</Link></>
                : "No templates match your search."}
            </div>
          )}
          {filtered.map(t => (
            <button
              key={t.id}
              type="button"
              disabled={assigningId === t.id}
              onClick={() => assign(t.id)}
              className="w-full text-left px-5 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-3 hover:bg-slate-50 disabled:opacity-50"
              data-testid={`c360-vault-pick-${t.id}`}
            >
              <FileText className="h-5 w-5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate flex items-center gap-2">
                  {t.name}
                  {t.is_acroform && <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-widest">Smart-Fill</span>}
                </div>
                <div className="text-[11px] text-slate-500">{(t.fields || []).length} field{(t.fields || []).length === 1 ? "" : "s"}</div>
              </div>
              {assigningId === t.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
                  <Plus className="h-3.5 w-3.5" /> Add
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Messages ───────────────────────────────────────────────────────────
function MessagesCard({ threads, clientName }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="c360-messages">
      <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-slate-500" /> Messages
      </h2>
      {threads.length === 0 ? (
        <div className="mt-3 text-sm text-slate-500">No messages with {clientName} yet.</div>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {threads.map(t => (
            <li key={t.transaction_id}>
              <Link
                to={`/messages?transaction=${t.transaction_id}&client=${encodeURIComponent(clientName || "")}`}
                className="block py-3 hover:bg-slate-50 -mx-2 px-2 rounded"
                data-testid={`c360-thread-${t.transaction_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-slate-500 truncate uppercase tracking-widest font-semibold">{t.transaction_address || "Transaction"}</div>
                  {t.unread_count > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold" data-testid={`c360-thread-unread-${t.transaction_id}`}>
                      {t.unread_count}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-slate-800 truncate">
                  <span className="text-slate-500">{t.last_sender === "agent" ? "You:" : "Them:"} </span>
                  {t.last_body || "(no messages)"}
                </div>
                {t.last_created_at && (
                  <div className="text-[11px] text-slate-400 mt-0.5">{new Date(t.last_created_at).toLocaleString()}</div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Listings ───────────────────────────────────────────────────────────
function ListingsCard({ listings, highlightId }) {
  const scrollRef = useRef(null);
  // Auto-scroll + pulse the highlighted listing when we land here from a
  // notification like "Client liked / requested a showing".
  useEffect(() => {
    if (!highlightId) return;
    const el = document.querySelector(`[data-testid='c360-listing-${highlightId}']`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("client-row-highlight");
      const t = setTimeout(() => el.classList.remove("client-row-highlight"), 2500);
      return () => clearTimeout(t);
    }
  }, [highlightId, listings]);
  return (
    <section ref={scrollRef} className="rounded-lg border border-slate-200 bg-white p-5" data-testid="c360-listings">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900 flex items-center gap-2">
          <HomeIcon className="h-4 w-4 text-slate-500" /> Recommendations
        </h2>
        <Link to="/listings" className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 uppercase tracking-widest inline-flex items-center gap-1" data-testid="c360-listings-add">
          <Plus className="h-3 w-3" /> Add
        </Link>
      </div>
      {listings.length === 0 ? (
        <div className="mt-3 text-sm text-slate-500">Nothing shared yet.</div>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {listings.map(l => {
            const isHighlighted = l.id === highlightId;
            return (
            <li
              key={l.id}
              className={`py-3 flex items-start gap-3 -mx-2 px-2 rounded transition-colors ${isHighlighted ? "bg-amber-50 ring-1 ring-amber-300" : ""}`}
              data-testid={`c360-listing-${l.id}`}
            >
              {l.image_url ? (
                <img src={l.image_url} alt="" className="h-12 w-16 rounded object-cover bg-slate-100 shrink-0" />
              ) : (
                <div className="h-12 w-16 rounded bg-slate-100 flex items-center justify-center text-slate-400 shrink-0">
                  <HomeIcon className="h-5 w-5" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-slate-900 text-sm truncate hover:underline block"
                  data-testid={`c360-listing-open-${l.id}`}
                >
                  {l.title || safeHost(l.url)}
                </a>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  {l.price_hint && <span className="font-semibold text-slate-700">{l.price_hint}</span>}
                  {l.liked_at && <span className="inline-flex items-center gap-1 text-rose-600 font-medium"><Heart className="h-3 w-3 fill-rose-500" /> Liked</span>}
                  {l.showing_requested_at && <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><CalendarClock className="h-3 w-3" /> Showing</span>}
                </div>
              </div>
              <a href={l.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded text-slate-400 hover:text-slate-700" title="Open">
                <ExternalLink className="h-4 w-4" />
              </a>
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ── Activity history ──────────────────────────────────────────────────
const ACTIVITY_LABEL = {
  showing_request: { label: "Showing requested", icon: CalendarClock, tone: "text-emerald-700" },
  property_save: { label: "Liked a property", icon: Heart, tone: "text-rose-600" },
  property_view: { label: "Viewed a property", icon: Eye, tone: "text-slate-600" },
  portal_login: { label: "Signed into portal", icon: UserIcon, tone: "text-slate-600" },
  email_open: { label: "Opened your email", icon: Mail, tone: "text-slate-600" },
  reply: { label: "Replied", icon: MessageSquare, tone: "text-slate-600" },
  call: { label: "Called", icon: Phone, tone: "text-slate-600" },
  text: { label: "Texted", icon: MessageSquare, tone: "text-slate-600" },
};

function ActivityCard({ activities }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="c360-activity">
      <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900 flex items-center gap-2">
        <ActivityIcon className="h-4 w-4 text-slate-500" /> Activity history
      </h2>
      {activities.length === 0 ? (
        <div className="mt-3 text-sm text-slate-500">Nothing logged yet.</div>
      ) : (
        <ul className="mt-3 space-y-3">
          {activities.map(a => {
            const meta = ACTIVITY_LABEL[a.kind] || { label: a.kind.replace(/_/g, " "), icon: ActivityIcon, tone: "text-slate-600" };
            const Icon = meta.icon;
            return (
              <li key={a.id} className="flex items-start gap-3" data-testid={`c360-activity-${a.id}`}>
                <div className={`shrink-0 mt-0.5 ${meta.tone}`}><Icon className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-800 uppercase tracking-widest">{meta.label}</div>
                  {a.body && <div className="text-sm text-slate-700 mt-0.5 truncate">{a.body}</div>}
                  <div className="text-[11px] text-slate-400 mt-0.5">{new Date(a.occurred_at).toLocaleString()}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ── PDF preview modal ─────────────────────────────────────────────────
function safeHost(u) {
  try { return new URL(u).hostname; } catch { return u; }
}
